import { useState, type ReactNode } from "react";
import type { SiteConfig } from "../site-config";
import type {
  ChatMessageData,
  ContactDetails,
  InquirySummary,
} from "../types";
import { BrandHeader } from "./BrandHeader";
import { CheckIcon } from "./Icons";

interface SuccessScreenProps {
  inquiryId: string;
  messages: ChatMessageData[];
  contact: ContactDetails;
  emailStatus: "sent" | "demo";
  summary?: InquirySummary;
  siteConfig: SiteConfig;
}

interface DemoEmailPreviewProps {
  inquiryId: string;
  messages: ChatMessageData[];
  contact: ContactDetails;
  summary: InquirySummary;
}

function PreviewSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="email-preview-section">
      <h3>{title}</h3>
      <div>{children}</div>
    </section>
  );
}

function DemoEmailPreview({
  inquiryId,
  messages,
  contact,
  summary,
}: DemoEmailPreviewProps) {
  return (
    <section className="demo-email-preview" aria-labelledby="email-preview-title">
      <div className="demo-email-notice">
        <strong>デモ表示</strong>
        <span>実際のメールは送信されていません</span>
      </div>
      <div className="email-preview-meta">
        <p><span>送信先</span> 管理者メールアドレス</p>
        <p><span>件名</span> 【新規お問い合わせ】{contact.company}｜AIヒアリングが完了しました</p>
      </div>
      <article className="email-preview-paper">
        <p className="email-preview-eyebrow">お問い合わせAI</p>
        <h2 id="email-preview-title">新しいお問い合わせが届きました</h2>
        <p className="email-preview-intro">
          一次商談の準備に必要な内容をAIが要約しました。
        </p>
        <PreviewSection title="相談概要">{summary.overview}</PreviewSection>
        <PreviewSection title="現状・課題">{summary.currentSituation}</PreviewSection>
        <PreviewSection title="希望・条件">{summary.desiredOutcome}</PreviewSection>
        {summary.acquisitionFields?.length ? (
          <PreviewSection title="獲得情報">
            <dl className="email-preview-contact">
              {summary.acquisitionFields.map((field) => <div key={field.key}><dt>{field.label}</dt><dd>{field.value}</dd></div>)}
            </dl>
          </PreviewSection>
        ) : null}
        <PreviewSection title="商談時のポイント">
          {summary.salesNotes}<br />緊急度：{summary.urgency}
        </PreviewSection>
        <PreviewSection title="商談時に確認する項目">
          {summary.unansweredQuestions.length
            ? summary.unansweredQuestions.map((item) => <p key={item}>・{item}</p>)
            : "特になし"}
        </PreviewSection>
        <PreviewSection title="お客様情報">
          <dl className="email-preview-contact">
            <div><dt>会社名</dt><dd>{contact.company}</dd></div>
            <div><dt>お名前</dt><dd>{contact.name}</dd></div>
            <div><dt>メール</dt><dd>{contact.email}</dd></div>
            <div><dt>電話</dt><dd>{contact.phone || "未入力"}</dd></div>
          </dl>
        </PreviewSection>
        <details className="email-preview-transcript">
          <summary>会話全文を表示</summary>
          {messages.map((message) => (
            <p key={message.id}>
              <strong>{message.role === "assistant" ? "AI" : "お客様"}</strong>
              <span>{message.content}</span>
            </p>
          ))}
        </details>
        <p className="email-preview-id">受付番号 {inquiryId}</p>
      </article>
    </section>
  );
}

export function SuccessScreen({
  inquiryId,
  messages,
  contact,
  emailStatus,
  summary,
  siteConfig,
}: SuccessScreenProps) {
  const [showTranscript, setShowTranscript] = useState(false);
  const isDemo = emailStatus === "demo";

  return (
    <div className="page-shell success-page">
      <BrandHeader siteConfig={siteConfig} />
      <main className="success-main">
        <div className="success-icon">
          <CheckIcon />
        </div>
        <h1>{isDemo ? "デモが完了しました" : "ありがとうございました"}</h1>
        <p className="success-lead">
          {isDemo ? (
            <>実際のメールは送信せず、管理者へ届く内容を画面で確認できます。</>
          ) : (
            <>
              お問い合わせを受け付けました。内容を確認のうえ、
              <br />
              担当者よりご連絡いたします。
            </>
          )}
        </p>
        <section className="receipt" aria-label="送信情報">
          <h2>{isDemo ? "メール結果を作成しました" : "送信が完了しました"}</h2>
          <p>受付番号：{inquiryId}</p>
          <strong>
            {isDemo ? "営業デモ用のため、メール送信は行っていません" : "通常1〜2営業日以内にご連絡します"}
          </strong>
        </section>
        {isDemo && summary ? (
          <DemoEmailPreview
            inquiryId={inquiryId}
            messages={messages}
            contact={contact}
            summary={summary}
          />
        ) : null}
        {!isDemo ? (
          <button
            className="transcript-toggle"
            type="button"
            onClick={() => setShowTranscript((visible) => !visible)}
            aria-expanded={showTranscript}
          >
            入力内容を確認する <span aria-hidden="true">›</span>
          </button>
        ) : null}
        {!isDemo && showTranscript ? (
          <section className="transcript-review">
            <h2>お客様情報</h2>
            <dl>
              <div><dt>会社名</dt><dd>{contact.company}</dd></div>
              <div><dt>お名前</dt><dd>{contact.name}</dd></div>
              <div><dt>メール</dt><dd>{contact.email}</dd></div>
              {contact.phone ? <div><dt>電話</dt><dd>{contact.phone}</dd></div> : null}
            </dl>
            <h2>会話内容</h2>
            {messages.map((message) => (
              <p key={message.id}>
                <strong>
                  {message.role === "assistant" ? siteConfig.companyName : "お客様"}
                </strong>
                {message.content}
              </p>
            ))}
          </section>
        ) : null}
      </main>
      <footer className="privacy-footer">入力内容はお問い合わせへの対応にのみ利用します</footer>
    </div>
  );
}
